import { ArrowRight, CalendarClock, Check, CreditCard, Home, ShieldAlert, Wallet } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "../shared/Badge";
import { multipropriedadeConfig } from "../../config/niches/multipropriedade";
import { formatBRL } from "../../lib/format";
import content from "../../data/content.json";

interface Module {
  id: string;
  title: string;
  description: string;
  to?: string;
}

const { solutions } = content.home;
const MODULES: Module[] = solutions.modules;
const ICONS: Record<string, LucideIcon> = { gateways: Wallet, "vazamento-dados": ShieldAlert, chargebacks: CreditCard, locacao: Home };

function FeaturedCard() {
  return (
    <article data-testid="solution-card-multipropriedade" className="flex h-full flex-col justify-between rounded-md border border-emerald-500/40 bg-slate-900 p-8 transition-colors duration-200 hover:border-emerald-500">
      <div>
        <div className="flex items-center justify-between gap-4">
          <CalendarClock aria-hidden="true" className="h-8 w-8 text-emerald-500" />
          <Badge testId="solution-multipropriedade-status">Disponível</Badge>
        </div>
        <h3 className="mt-8 text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl">{multipropriedadeConfig.name}</h3>
        <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
          {solutions.featuredDescription}
        </p>
      </div>
      <ul data-testid="solution-multipropriedade-highlights" className="mt-8 space-y-3 border-t border-slate-800 pt-6">
        {solutions.featuredHighlights.map((h) => (
          <li key={h} className="flex gap-3 text-sm text-slate-300">
            <Check aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{h}
          </li>
        ))}
      </ul>
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-mono text-sm text-slate-400">
          a partir de <span className="text-lg font-semibold text-slate-100">{formatBRL(multipropriedadeConfig.price)}</span>
        </p>
        <Link to={`/${multipropriedadeConfig.slug}`} data-testid="solution-multipropriedade-cta" className="btn-primary">
          Iniciar protocolo <ArrowRight aria-hidden="true" className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
}

function ModuleCard({ module }: { module: Module }) {
  const Icon = ICONS[module.id] ?? Wallet;
  return (
    <article data-testid={`solution-card-${module.id}`} className="flex h-full flex-col justify-between rounded-md border border-slate-800 bg-slate-900 p-6 transition-colors duration-200 hover:border-slate-700">
      <div>
        <div className="flex items-center justify-between gap-4">
          <Icon aria-hidden="true" className="h-6 w-6 text-slate-400" />
          {module.to ? (
            <Badge testId={`solution-${module.id}-badge`}>Disponível</Badge>
          ) : (
            <Badge tone="muted" testId={`solution-${module.id}-badge`}>Em breve</Badge>
          )}
        </div>
        <h3 className="mt-6 text-lg font-semibold text-slate-100">{module.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">{module.description}</p>
      </div>
      {module.to && (
        <Link to={module.to} data-testid={`solution-${module.id}-cta`} className="btn-primary mt-6 w-full">
          Iniciar protocolo <ArrowRight aria-hidden="true" className="h-4 w-4" />
        </Link>
      )}
    </article>
  );
}

export function Solutions() {
  return (
    <section id="solucoes" data-testid="home-solutions" className="scroll-mt-28 border-b border-slate-800">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-500">{solutions.eyebrow}</p>
        <h2 className="mt-3 max-w-2xl text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl">{solutions.heading}</h2>
        <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-2">
          <FeaturedCard />
          <div className="grid items-stretch gap-6 sm:grid-cols-2">
            {MODULES.map((m) => <ModuleCard key={m.id} module={m} />)}
          </div>
        </div>
      </div>
    </section>
  );
}
